import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DoctorAPI } from "../../services/doctor.service";
import type { BlockType, DoctorBlockTimeCreatePayload, DoctorBlockTimeCreateResponse, RecurrenceRule } from "../../services/doctor.service";
import { DateInput } from "../ui/DateInput";

type WeekdayCode = NonNullable<RecurrenceRule["byweekday"]>[number];

// Monday-first, matching the backend's weekday numbering.
const WEEKDAYS: { code: WeekdayCode; label: string; name: string }[] = [
  { code: "MO", label: "Mo", name: "Monday" }, { code: "TU", label: "Tu", name: "Tuesday" },
  { code: "WE", label: "We", name: "Wednesday" }, { code: "TH", label: "Th", name: "Thursday" },
  { code: "FR", label: "Fr", name: "Friday" }, { code: "SA", label: "Sa", name: "Saturday" },
  { code: "SU", label: "Su", name: "Sunday" },
];

// Mirror the backend's RecurrenceRule limits so a bad value is caught here
// with a clear message instead of as a raw 422.
const MAX_REPEAT_COUNT = 260;
const MAX_SERIES_DAYS = 5 * 365;

/** "YYYY-MM-DD" shifted by n local days. */
const addDaysToDateStr = (dateStr: string, days: number) => {
  const d = new Date(`${dateStr}T00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** The instant local midnight starts dateStr. */
const localMidnightISO = (dateStr: string) => new Date(`${dateStr}T00:00`).toISOString();

const weekdayOf = (dateStr: string) => WEEKDAYS[(new Date(`${dateStr}T00:00`).getDay() + 6) % 7];

/** Today as a local "YYYY-MM-DD" - toISOString() would give the UTC date,
 *  which in IST is still yesterday until 05:30. */
const localTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Working-day defaults when the modal isn't opened from a calendar drag.
const DEFAULT_START_TIME = "09:00";
const DEFAULT_END_TIME = "17:00";

const FIELD_CLASS = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white";

interface Props {
  isOpen: boolean;
  doctorId: string;
  doctorName: string;
  onClose: () => void;
  onSuccess: () => void;
  /** Pre-filled from a calendar drag-select, as datetime-local strings. */
  initialStart?: string;
  initialEnd?: string;
}

export const BlockDoctorTimeModal: React.FC<Props> = ({
  isOpen, doctorId, doctorName, onClose, onSuccess, initialStart, initialEnd,
}) => {
  const navigate = useNavigate();
  // Each end of the range stays one datetime-local string ("YYYY-MM-DDTHH:MM"):
  // the date picker and the time picker below each edit their half of it.
  const [startTime, setStartTime] = useState(initialStart ?? `${localTodayStr()}T${DEFAULT_START_TIME}`);
  const [endTime, setEndTime] = useState(initialEnd ?? `${localTodayStr()}T${DEFAULT_END_TIME}`);
  const [reason, setReason] = useState("");
  const [blockTypes, setBlockTypes] = useState<BlockType[]>([]);
  const [blockTypeId, setBlockTypeId] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [isVacation, setIsVacation] = useState(false);
  const [patientNote, setPatientNote] = useState("");
  const [repeats, setRepeats] = useState(false);
  const [freq, setFreq] = useState<RecurrenceRule["freq"]>("weekly");
  const [byweekday, setByweekday] = useState<WeekdayCode[]>([]);
  const [endType, setEndType] = useState<"until" | "count">("count");
  const [until, setUntil] = useState("");
  const [count, setCount] = useState("10");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DoctorBlockTimeCreateResponse | null>(null);

  // The calendar page keeps this component permanently mounted and just
  // toggles `isOpen` (unlike DoctorListPage's usage, which conditionally
  // mounts/unmounts it) - so the useState initializers above only run once,
  // on the very first mount, and never see a later drag-select's
  // initialStart/initialEnd. Re-seed every field fresh each time the modal
  // actually opens, so both mount patterns behave the same way.
  useEffect(() => {
    if (!isOpen) return;
    // A drag-selected range wins; otherwise today, 09:00-17:00.
    const today = localTodayStr();
    setStartTime(initialStart ?? `${today}T${DEFAULT_START_TIME}`);
    setEndTime(initialEnd ?? `${today}T${DEFAULT_END_TIME}`);
    setReason("");
    setBlockTypeId("");
    setAllDay(false);
    setIsVacation(false);
    setPatientNote("");
    setRepeats(false);
    setFreq("weekly");
    setByweekday([]);
    setEndType("count");
    setUntil("");
    setCount("10");
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialStart, initialEnd]);

  useEffect(() => {
    if (!isOpen) return;
    DoctorAPI.listBlockTypes().then(setBlockTypes).catch(() => setBlockTypes([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // All-day mode edits only the date part of startTime/endTime, keeping the
  // time part so switching All day back off restores what was there.
  const startDate = startTime.slice(0, 10);
  const endDate = endTime.slice(0, 10);
  const startDay = startDate ? weekdayOf(startDate) : null;
  const setDatePart = (value: string, current: string, set: (v: string) => void) =>
    set(value ? `${value}T${current.slice(11, 16) || "00:00"}` : "");
  const setTimePart = (value: string, current: string, set: (v: string) => void) =>
    set(`${current.slice(0, 10) || localTodayStr()}T${value || "00:00"}`);
  const todayStr = localTodayStr();

  // Moving the start date past the end date drags the end date along with it.
  const handleStartDateChange = (value: string) => {
    setDatePart(value, startTime, setStartTime);
    if (value && (!endDate || endDate < value)) setDatePart(value, endTime || startTime, setEndTime);
  };

  // doctorName may already carry the prefix - avoids "Dr. Dr. Name".
  const displayName = doctorName.replace(/^Dr\.\s*/i, "");

  const toggleWeekday = (code: WeekdayCode) => {
    setByweekday((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  // The block itself is the series' first occurrence, so its own weekday is
  // always a repeat day - pre-selected here, and required on submit.
  const ensureStartDaySelected = () => {
    if (startDay) setByweekday((prev) => (prev.includes(startDay.code) ? prev : [...prev, startDay.code]));
  };

  const handleSubmit = async () => {
    if (!startTime || (!allDay && !endTime) || !reason.trim()) {
      toast.error(allDay ? "Fill in a start date and a reason" : "Fill in start time, end time, and a reason");
      return;
    }
    // All day = whole local days: midnight of the first day up to the
    // midnight after the last one (end exclusive) - the server snaps all-day
    // blocks to the same clinic-local boundaries.
    const startISO = allDay ? localMidnightISO(startDate) : new Date(startTime).toISOString();
    const endISO = allDay
      ? localMidnightISO(addDaysToDateStr(endDate || startDate, 1))
      : new Date(endTime).toISOString();
    if (new Date(endISO) <= new Date(startISO)) {
      toast.error(allDay ? "The last day can't be before the first day" : "End time must be after the start time");
      return;
    }
    if (new Date(endISO) <= new Date()) {
      toast.error("Blocked time must end in the future");
      return;
    }
    if (repeats) {
      if (freq === "weekly" && byweekday.length === 0) {
        toast.error("Pick at least one weekday to repeat on");
        return;
      }
      if (freq === "weekly" && startDay && !byweekday.includes(startDay.code)) {
        toast.error(`Repeat days must include ${startDay.name}, the day this block starts on`);
        return;
      }
      if (endType === "until") {
        if (!until) {
          toast.error("Pick an end date for the recurrence");
          return;
        }
        if (until <= startDate) {
          toast.error("The repeat end date must be after the block's start date");
          return;
        }
        if (until > addDaysToDateStr(startDate, MAX_SERIES_DAYS)) {
          toast.error("A repeating block can run for at most 5 years");
          return;
        }
      }
      if (endType === "count") {
        const n = Number(count);
        if (!count || !Number.isInteger(n) || n < 1 || n > MAX_REPEAT_COUNT) {
          toast.error(`Enter how many times this repeats (1–${MAX_REPEAT_COUNT})`);
          return;
        }
      }
    }
    setLoading(true);
    try {
      const payload: DoctorBlockTimeCreatePayload = {
        start_time: startISO,
        end_time: endISO,
        reason: reason.trim(),
        block_type_id: blockTypeId || undefined,
        all_day: allDay,
        is_vacation: isVacation,
        patient_facing_note: patientNote.trim() || undefined,
      };
      if (repeats) {
        payload.recurrence = {
          freq,
          byweekday: freq === "weekly" ? byweekday : undefined,
          // A plain inclusive date - converting it to an instant made
          // "until 21 Oct" mean 21 Oct 00:00 UTC and dropped that day.
          until: endType === "until" ? until : undefined,
          count: endType === "count" ? Number(count) : undefined,
        };
      }
      const response = await DoctorAPI.createBlockTime(doctorId, payload);
      setResult(response);
      onSuccess();
      if (response.conflicting_appointments.length === 0) {
        toast.success(
          response.occurrences_materialized > 0
            ? `Time blocked (+${response.occurrences_materialized} repeat occurrence${response.occurrences_materialized !== 1 ? "s" : ""})`
            : "Time blocked",
        );
        onClose();
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to block time");
    } finally {
      setLoading(false);
    }
  };

  const handleResolveConflicts = () => {
    if (!result) return;
    onClose();
    navigate(`/appointments/bulk-reschedule?source=doctor_block&doctor_id=${doctorId}&block_time_id=${result.block.block_id}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Block Time — Dr. {displayName}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none">×</button>
        </div>

        {!result ? (
          <div className="px-6 py-5 space-y-4 overflow-y-auto">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{allDay ? "First day" : "Start"}</label>
                <div className={allDay ? "" : "grid grid-cols-2 gap-3"}>
                  <DateInput
                    name={allDay ? "First day" : "Start date"}
                    value={startDate}
                    min={todayStr}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className={FIELD_CLASS}
                  />
                  {!allDay && (
                    <input
                      type="time"
                      aria-label="Start time"
                      value={startTime.slice(11, 16)}
                      onChange={(e) => setTimePart(e.target.value, startTime, setStartTime)}
                      className={`${FIELD_CLASS} text-slate-700`}
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{allDay ? "Last day" : "End"}</label>
                <div className={allDay ? "" : "grid grid-cols-2 gap-3"}>
                  <DateInput
                    name={allDay ? "Last day" : "End date"}
                    value={endDate || startDate}
                    min={startDate || todayStr}
                    onChange={(e) => setDatePart(e.target.value, endTime || startTime, setEndTime)}
                    className={FIELD_CLASS}
                  />
                  {!allDay && (
                    <input
                      type="time"
                      aria-label="End time"
                      value={endTime.slice(11, 16)}
                      onChange={(e) => setTimePart(e.target.value, endTime || startTime, setEndTime)}
                      className={`${FIELD_CLASS} text-slate-700`}
                    />
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Type</label>
              <select
                value={blockTypeId}
                onChange={(e) => setBlockTypeId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                <option value="">No specific type</option>
                {blockTypes.map((t) => (
                  <option key={t.block_type_id} value={t.block_type_id}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Reason (staff-only)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Sick leave, vacation…"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
              />
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                All day
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={isVacation} onChange={(e) => setIsVacation(e.target.checked)} />
                Vacation
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={repeats}
                  onChange={(e) => {
                    setRepeats(e.target.checked);
                    if (e.target.checked && freq === "weekly") ensureStartDaySelected();
                  }}
                />
                Repeats
              </label>
            </div>

            {isVacation && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Note shown to affected patients (optional)</label>
                <textarea
                  value={patientNote}
                  onChange={(e) => setPatientNote(e.target.value)}
                  rows={2}
                  placeholder="e.g. Dr. is on leave — we'll help you reschedule"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
              </div>
            )}

            {repeats && (
              <div className="rounded-xl border border-slate-200 p-3 space-y-3">
                <div className="flex gap-3 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input type="radio" checked={freq === "weekly"} onChange={() => { setFreq("weekly"); ensureStartDaySelected(); }} /> Weekly
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="radio" checked={freq === "daily"} onChange={() => setFreq("daily")} /> Daily
                  </label>
                </div>
                {freq === "weekly" && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {WEEKDAYS.map((w) => (
                        <button
                          key={w.code}
                          type="button"
                          aria-pressed={byweekday.includes(w.code)}
                          onClick={() => toggleWeekday(w.code)}
                          className={`w-8 h-8 rounded-full text-xs font-medium transition ${
                            byweekday.includes(w.code) ? "bg-cyan-500 text-white" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {w.label}
                        </button>
                      ))}
                    </div>
                    {startDay && (
                      <p className="text-[11px] text-slate-400">Must include {startDay.name}, the day this block starts on.</p>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input type="radio" checked={endType === "count"} onChange={() => setEndType("count")} /> After
                  </label>
                  <input
                    type="number" min={1} max={MAX_REPEAT_COUNT} value={count} disabled={endType !== "count"}
                    onChange={(e) => setCount(e.target.value)}
                    className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm disabled:opacity-40"
                  />
                  <span className="text-slate-500">times</span>
                  <label className="flex items-center gap-1.5 ml-2">
                    <input type="radio" checked={endType === "until"} onChange={() => setEndType("until")} /> Until
                  </label>
                  <input
                    type="date" aria-label="Repeat until" value={until} disabled={endType !== "until"}
                    min={startDate ? addDaysToDateStr(startDate, 1) : undefined}
                    max={startDate ? addDaysToDateStr(startDate, MAX_SERIES_DAYS) : undefined}
                    onChange={(e) => setUntil(e.target.value)}
                    className="rounded-lg border border-slate-200 px-2 py-1 text-sm disabled:opacity-40"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50 transition">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-cyan-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-600 disabled:opacity-40 transition"
              >
                {loading ? "Blocking…" : "Block Time"}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <p className="text-sm text-amber-800">
                {result.conflicting_appointments.length} appointment{result.conflicting_appointments.length !== 1 ? "s" : ""}{" "}
                conflict with this block{result.block.recurrence_rule ? " and its repeats" : ""} — resolve now?
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50 transition">
                Later
              </button>
              <button
                onClick={handleResolveConflicts}
                className="flex-1 bg-amber-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-amber-600 transition"
              >
                Resolve Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
